import { deploymentReferences, diagramCards, docEntries } from '../content/content-manifest';
import type { ActionLink } from '../page-elements';
import { ActionRow, DocsCard } from '../page-elements.tsrx';

const STATS = [
  { label: 'Doc pages', value: String(docEntries.length), icon: '📄' },
  { label: 'Deployment refs', value: String(deploymentReferences.length), icon: '🚀' },
  { label: 'Generated views', value: String(diagramCards.length), icon: '🗺️' },
  { label: 'Deployable app', value: 'Docker-ready', icon: '🐳' },
];

function deploymentActions(reference: (typeof deploymentReferences)[number]): ActionLink[] {
  return [
    { label: 'Read notes', href: `/${reference.routePath}`, variant: 'primary' },
    { label: 'Open diagram', href: reference.diagramHref, newTab: true },
    ...(reference.flowHref ? [{ label: 'Open flow', href: reference.flowHref, newTab: true }] : []),
  ];
}

function viewActions(card: (typeof diagramCards)[number]): ActionLink[] {
  return [
    { label: 'Open asset', href: card.href, newTab: true, variant: 'primary' },
    ...(card.routePath ? [{ label: 'Open notes', href: `/${card.routePath}` }] : []),
  ];
}

export default function Home() @{
  <div class="docs-page">
    <section class="docs-hero docs-hero--home">
      <div class="docs-hero-top">
        <span class="docs-eyebrow">Crown system docs</span>
        <h1>
          Architecture, deployment,
          <br />
          {'and runtime maps for the whole platform.'}
        </h1>
        <p class="docs-hero-copy">Generated graph views, deployment references, operating guides, and auth runtime notes — packaged into one deployable docs experience.</p>
        <ActionRow actions={[
          { label: 'Open explorer', href: '/explorer/' },
          { label: 'Architecture hub', href: '/architecture', variant: 'primary' },
          { label: 'DSL playground', href: '/dsl-playground' },
          { label: 'Publish guide', href: '/guides/publishing-docs' },
        ]} />
      </div>
      <div class="docs-stats">
        @for (const stat of STATS; key stat.label) {
          <article class="docs-stat">
            <span aria-hidden="true" class="docs-stat-icon">{stat.icon}</span>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </article>
        }
      </div>
    </section>

    <section class="docs-section" aria-labelledby="home-deployment">
      <div class="docs-section-heading">
        <div>
          <h2 id="home-deployment">Deployment reference patterns</h2>
          <p class="docs-section-copy">Open the model that matches the hosting shape you actually care about, then use the generated flows for communication order and rollout order.</p>
        </div>
      </div>
      <div class="docs-card-grid">
        @for (const reference of deploymentReferences; key reference.id) {
          <DocsCard eyebrow="Deployment" title={reference.title} description={reference.summary} tags={reference.tags} actions={deploymentActions(reference)} />
        }
      </div>
    </section>

    <section class="docs-section" aria-labelledby="home-views">
      <div class="docs-section-heading">
        <div>
          <h2 id="home-views">Generated system views</h2>
          <p class="docs-section-copy">These are the fast-path diagrams you will actually reopen when someone asks “how does this thing talk to that thing?” five minutes before a deployment.</p>
        </div>
      </div>
      <div class="docs-card-grid">
        @for (const card of diagramCards; key card.id) {
          <DocsCard eyebrow="Generated" title={card.title} description={card.description} tags={card.tags} actions={viewActions(card)} />
        }
      </div>
    </section>

    <section class="docs-cta">
      <div class="docs-cta-main">
        <span class="docs-eyebrow">Deployable docs image</span>
        <h2>Build the latest docs, package the app, ship one container.</h2>
        <p>Regenerate architecture artefacts, build the frontend shell, and publish the current repo state as a proper site — one command, one image, one deploy.</p>
        <ActionRow actions={[
          { label: 'Open publish guide', href: '/guides/publishing-docs', variant: 'primary' },
          { label: 'Open docs README', href: '/readme' },
        ]} />
      </div>
      <aside class="docs-cta-side">
        <strong>Release rhythm</strong>
        <ul>
          <li>Regenerate docs after architecture changes.</li>
          <li>Check freshness before cutting an image.</li>
          <li>Build the docs-site image from the refreshed repo state.</li>
          <li>Deploy behind your public docs domain.</li>
        </ul>
      </aside>
    </section>
  </div>
}
